import { alpha, createTheme } from '@mui/material/styles';

const brandBlue = '#7dd3fc';
const brandGreen = '#8bffb0';
const brandSurface = '#0f172a';
const brandSurfaceAlt = '#172033';
const brandInk = '#e2e8f0';
const brandMuted = '#94a3b8';

export const appTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: brandBlue,
      light: '#bae6fd',
      dark: '#0ea5e9',
      contrastText: '#08111f',
    },
    secondary: {
      main: brandGreen,
      light: '#c7ffd6',
      dark: '#3bc26a',
      contrastText: '#06120b',
    },
    warning: {
      main: '#fbbf24',
    },
    error: {
      main: '#fb7185',
    },
    success: {
      main: '#4ade80',
    },
    background: {
      default: '#08111f',
      paper: brandSurface,
    },
    text: {
      primary: brandInk,
      secondary: brandMuted,
    },
    divider: alpha('#cbd5e1', 0.12),
  },
  spacing: 8,
  shape: {
    borderRadius: 20,
  },
  typography: {
    fontFamily: '"Manrope", "Segoe UI", sans-serif',
    h1: {
      fontSize: '3.4rem',
      fontWeight: 800,
      lineHeight: 1.05,
      letterSpacing: '-0.04em',
    },
    h2: {
      fontSize: '2.6rem',
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: '-0.04em',
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 780,
      lineHeight: 1.15,
      letterSpacing: '-0.03em',
    },
    h4: {
      fontSize: '1.6rem',
      fontWeight: 760,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 720,
      lineHeight: 1.25,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 720,
      lineHeight: 1.3,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 640,
    },
    subtitle2: {
      fontSize: '0.92rem',
      fontWeight: 700,
      letterSpacing: '0.01em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#08111f',
          backgroundImage: 'radial-gradient(circle at top, rgba(125,211,252,0.18), transparent 36%), linear-gradient(180deg, #08111f 0%, #0f172a 48%, #08111f 100%)',
        },
        '#root': {
          minHeight: '100vh',
        },
        '@media print': {
          body: {
            backgroundImage: 'none',
            backgroundColor: '#fff',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backdropFilter: 'blur(18px)',
          backgroundColor: alpha('#08111f', 0.82),
          borderBottom: `1px solid ${alpha('#cbd5e1', 0.08)}`,
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${alpha('#cbd5e1', 0.08)}`,
          boxShadow: '0 24px 60px rgba(2, 8, 23, 0.32)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          border: `1px solid ${alpha('#cbd5e1', 0.08)}`,
          backgroundImage: `linear-gradient(180deg, ${alpha('#172033', 0.96)} 0%, ${alpha('#0f172a', 0.98)} 100%)`,
          boxShadow: '0 24px 60px rgba(2, 8, 23, 0.28)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          paddingInline: 18,
          minHeight: 44,
        },
        containedPrimary: {
          backgroundImage: 'linear-gradient(135deg, #7dd3fc 0%, #8bffb0 100%)',
          color: '#06111f',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: alpha(brandSurface, 0.96),
          backgroundImage: `linear-gradient(180deg, ${alpha('#172033', 0.98)} 0%, ${alpha('#0f172a', 0.98)} 100%)`,
          borderRight: `1px solid ${alpha('#cbd5e1', 0.08)}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          backgroundColor: alpha(brandSurfaceAlt, 0.8),
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          minHeight: 52,
          '&.Mui-selected': {
            backgroundColor: alpha(brandBlue, 0.18),
            border: `1px solid ${alpha(brandBlue, 0.3)}`,
          },
        },
      },
    },
  },
});
